import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { Blockchain } from "@anaconda/core/blockchain/types"
import type { CurrencyId } from "@anaconda/core/price/types"
import { Trans } from "@lingui/react/macro"
import {
  type SolanaCollectionNftStandard,
  type SolanaCollectionOfferRequest,
  collectionOfferFactoryReatom,
} from "@rarible_int/anaconda_sdk/solana"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import { molecule } from "bunshi"
import { SolanaSdkContainerMoleculeReatom } from "../../blockchains/solana-sdk-container-molecule.reatom"
import { AppToast, SuccessViewTransaction } from "../../toasts/app-toast"
import { getTransactionErrorMessage } from "../events/transaction-event"

export interface SolanaCollectionOfferActionParams {
  collectionAddress: string
  nftStandard: SolanaCollectionNftStandard
  currency: CurrencyId
  price: string
}

export const SolanaCollectionOfferTransactionMoleculeReatom = molecule(
  (mol) => {
    const { sdkContainerAtom } = mol(SolanaSdkContainerMoleculeReatom)
    const { solanaAddress: solanaAddressAtom } = mol(
      WalletConnectMoleculeReatom,
    )

    const collectionOfferActionFactory = () => {
      const collectionOfferAction = action(
        async (params: SolanaCollectionOfferActionParams) => {
          const { collectionAddress, nftStandard, currency, price } = params
          const from = solanaAddressAtom()
          const container = sdkContainerAtom()

          if (!from) {
            throw new Error(
              "Solana wallet address is not available for collection offer",
            )
          }

          const collectionOffer = collectionOfferFactoryReatom(container)
          const request: SolanaCollectionOfferRequest = {
            from,
            collectionAddress,
            nftStandard,
            price: {
              currencyId: currency,
              value: price,
            },
          }

          const result = await wrap(collectionOffer(request))
          return { txId: result.txId }
        },
        "solanaCollectionOfferAction",
      ).extend(
        withAsync(),
        // onCall: show loading toast immediately (dialog closes right after submit)
        withCallHook(() => {
          sonnerToast.custom(
            (id) => (
              <AppToast.Pending
                id={id}
                title="Collection offer"
                main={<Trans>Collection offer</Trans>}
                second={<Trans>Pending...</Trans>}
              />
            ),
            { duration: Number.POSITIVE_INFINITY },
          )
        }),
      )

      collectionOfferAction.onReject.extend(
        withCallHook(({ error }) => {
          const message = getTransactionErrorMessage(error)
          sonnerToast.dismiss()
          sonnerToast.custom((id) => (
            <AppToast.Error
              id={id}
              title="Collection offer"
              main={<Trans>Collection offer failed</Trans>}
              second={
                <div className="font-semibold text-destructive">{message}</div>
              }
            />
          ))
        }),
      )

      collectionOfferAction.onFulfill.extend(
        withCallHook(({ payload }) => {
          const { txId } = payload
          sonnerToast.dismiss()
          sonnerToast.custom((id) => (
            <AppToast.Success
              id={id}
              title="Collection offer"
              main={<Trans>Collection offer sent</Trans>}
              second={
                <SuccessViewTransaction
                  transactionId={txId}
                  blockchain={Blockchain.SOLANA}
                />
              }
            />
          ))
        }),
      )

      return collectionOfferAction
    }

    return { collectionOfferActionFactory }
  },
)
